export const M4_UNIT_11 = {
  id: "m4-u11-health-illnesses",
  title: "Unidad 11: What's the Matter? / Salud y Enfermedades",
  topic: "Learn to talk about health problems and medical situations! Essential vocabulary for doctor visits, pharmacy, calling in sick to work. From simple 'I have a headache' to complex symptoms. Critical for living in Canada!",
  iconType: "health",
  grammarImage: "/grammar/grammar-m4-u11.jpg",
  grammarText: "I HAVE + pain/problem (I have a headache, I have the flu). My + body part + HURTS (My head hurts, My legs hurt). I AM/FEEL + adjective (I am sick, I feel tired). I AM NOT FEELING WELL = no me siento bien. Para médico: 'What's wrong?' o 'What's the matter?'",
  
  sets: [
    // SET 1: Basic Health Problems Vocabulary
    {
      id: "m4-u11-set1",
      title: "Set 1: Common Aches & Pains",
      questions: [
        {
          type: "match",
          q: "Match health problem to Spanish:",
          pairs: [
            { left: "headache", right: "dolor de cabeza" },
            { left: "stomachache", right: "dolor de estómago" },
            { left: "toothache", right: "dolor de muelas" }
          ],
          a: "completed",
          tip: "These are THE most common aches! '-ache' ending = dolor de [body part]!"
        },
        {
          type: "mc",
          q: "'I have a ___.' (dolor de cabeza)",
          options: ["headache", "head ache", "head pain"],
          a: "headache",
          tip: "Headache = ONE word! Not 'head ache' ❌. This is the #1 most common health complaint!"
        },
        {
          type: "input",
          q: "Complete: 'I have a ___.' (dolor de estómago)",
          a: "stomachache"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo dolor de muelas'",
          words: ["I", "have", "a", "toothache."],
          a: "I have a toothache."
        },
        {
          type: "mc",
          q: "Which structure? 'I ___ a backache.' (dolor de espalda)",
          options: ["have", "am", "has"],
          a: "have",
          tip: "'I HAVE a [ache]' = correct structure! Always 'I have' (not 'I am')!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ a headache.' (have/has)",
          a: "has"
        },
        {
          type: "mc",
          q: "'My son ___ an earache.' (dolor de oído)",
          options: ["has", "have", "is"],
          a: "has",
          tip: "Earache = dolor de oído. He/She/It = HAS. Common in children!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella tiene dolor de garganta'",
          words: ["She", "has", "a", "sore", "throat."],
          a: "She has a sore throat.",
          successtip: "✓ SORE THROAT! Different pattern! 🤒 Not 'throatache'! We say 'sore throat' (garganta adolorida). Super common cold symptom!"
        },
        {
          type: "input",
          q: "Complete: 'They ___ backaches.' (have/has - plural!)",
          a: "have"
        },
        {
          type: "mc",
          q: "ERROR: 'I am have headache' - Correction:",
          options: [
            "'I have a headache' (no 'am', need 'a')",
            "'I am headache'",
            "It's correct"
          ],
          a: "'I have a headache' (no 'am', need 'a')",
          tip: "Don't mix! 'I HAVE a headache' ✓ Not 'I am have' ❌. Need article 'a'!"
        }
      ]
    },

    // SET 2: Using "HURT" vs "HAVE"
    {
      id: "m4-u11-set2",
      title: "Set 2: My [body part] Hurts",
      questions: [
        {
          type: "match",
          q: "Match structure to example:",
          pairs: [
            { left: "I have + problem", right: "I have a headache" },
            { left: "My + body part + hurts", right: "My head hurts" },
            { left: "Both mean same!", right: "Same meaning, different structure" }
          ],
          a: "completed",
          tip: "TWO ways to say the same thing! 'I have a headache' = 'My head hurts' = mismo significado!"
        },
        {
          type: "mc",
          q: "Which is correct? (cabeza me duele)",
          options: ["My head hurts", "My head hurt", "My head is hurt"],
          a: "My head hurts",
          tip: "'My head HURTS' (with -s)! Singular body part = verb+s in present simple!"
        },
        {
          type: "input",
          q: "Complete: 'My stomach ___.' (hurts/hurt)",
          a: "hurts"
        },
        {
          type: "order",
          q: "Ordena: 'Me duelen las piernas'",
          words: ["My", "legs", "hurt."],
          a: "My legs hurt.",
          successtip: "✓ PLURAL! No -s! 🦵🦵 My legS HURT (no 'hurts'). Plural subject = no -s on verb! Different from singular!"
        },
        {
          type: "mc",
          q: "'My teeth ___.' (plural - me duelen los dientes)",
          options: ["hurt", "hurts", "is hurting"],
          a: "hurt",
          tip: "Teeth = PLURAL! My teeth HURT (no -s). Plural = no -s on verb!"
        },
        {
          type: "input",
          q: "Complete: 'His back ___.' (hurts/hurt - singular)",
          a: "hurts"
        },
        {
          type: "mc",
          q: "Same meaning: 'I have a toothache' = 'My tooth ___.'",
          options: ["hurts", "hurt", "aches"],
          a: "hurts",
          tip: "Both correct! 'I have a toothache' = 'My tooth hurts' = mismo significado!"
        },
        {
          type: "order",
          q: "Ordena: 'Me duele el estómago'",
          words: ["My", "stomach", "hurts."],
          a: "My stomach hurts."
        },
        {
          type: "input",
          q: "Rewrite: 'I have a headache' → 'My ___ ___.' (same meaning)",
          a: "head hurts"
        },
        {
          type: "mc",
          q: "Which is WRONG? 'My eyes ___.'",
          options: ["hurts (incorrect - should be 'hurt')", "hurt (correct)", "are hurting (correct - continuous)"],
          a: "hurts (incorrect - should be 'hurt')",
          tip: "Eyes = PLURAL! 'My eyes HURT' ✓ not 'hurts' ❌. Common mistake!"
        }
      ]
    },

    // SET 3: Feeling Sick/Ill
    {
      id: "m4-u11-set3",
      title: "Set 3: 'I'm Not Feeling Well'",
      questions: [
        {
          type: "match",
          q: "Match phrase to use:",
          pairs: [
            { left: "I'm not feeling well", right: "General - no me siento bien" },
            { left: "I'm sick/ill", right: "More direct - estoy enfermo" },
            { left: "I feel terrible", right: "Emphasis - me siento terrible" }
          ],
          a: "completed",
          tip: "'Not feeling well' = polite Canadian way! More common than 'I'm sick' at work!"
        },
        {
          type: "mc",
          q: "To boss/coworker: 'I can't come to work today. ___.'",
          options: ["I'm not feeling well", "I'm dying", "I'm sick"],
          a: "I'm not feeling well",
          successtip: "✓ PROFESSIONAL! Canadian politeness! 🇨🇦 'Not feeling well' = more polite than 'I'm sick'. Use at work!"
        },
        {
          type: "input",
          q: "Complete: 'She ___ feeling well today.' (is not - contraction)",
          a: "isn't"
        },
        {
          type: "order",
          q: "Ordena: 'No me siento bien hoy'",
          words: ["I'm", "not", "feeling", "well", "today."],
          a: "I'm not feeling well today."
        },
        {
          type: "mc",
          q: "'I ___ sick.' (estoy enfermo - using to be)",
          options: ["am", "have", "feel"],
          a: "am",
          tip: "'I AM sick' = estoy enfermo (state). Can also say 'I FEEL sick' = me siento enfermo!"
        },
        {
          type: "input",
          q: "Complete: 'He ___ ill.' (is/are - sick)",
          a: "is"
        },
        {
          type: "mc",
          q: "UK English vs US English: 'sick' vs '___'",
          options: ["ill (UK prefers this)", "sick (US prefers this)", "unwell"],
          a: "ill (UK prefers this)",
          tip: "UK = 'I'm ill' more common. US/Canada = 'I'm sick' more common. Both correct!"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento terrible'",
          words: ["I", "feel", "terrible."],
          a: "I feel terrible.",
          successtip: "✓ FEEL! State verb! 🤒 'I FEEL + adjective' = express how you feel. Terrible/awful/horrible = very sick!"
        },
        {
          type: "input",
          q: "Complete: 'They ___ feeling well.' (are not - contraction)",
          a: "aren't"
        },
        {
          type: "mc",
          q: "Phone call: Boss asks 'Are you okay?' You say:",
          options: [
            "'No, I'm not feeling well today'",
            "'I'm fine' (if you're actually sick)",
            "'I'm dying'"
          ],
          a: "'No, I'm not feeling well today'",
          tip: "Honest but professional! 'Not feeling well' = perfect response for calling in sick!"
        }
      ]
    },

    // SET 4: Common Illnesses
    {
      id: "m4-u11-set4",
      title: "Set 4: Flu, Cold & Common Illnesses",
      questions: [
        {
          type: "match",
          q: "Match illness to Spanish:",
          pairs: [
            { left: "cold", right: "resfriado/catarro" },
            { left: "flu", right: "gripe/influenza" },
            { left: "fever", right: "fiebre" }
          ],
          a: "completed",
          tip: "Cold = resfriado (menos grave). Flu = gripe (más grave). THE cold vs A cold!"
        },
        {
          type: "mc",
          q: "'I have ___ cold.' (Which article?)",
          options: ["a", "the", "no article"],
          a: "a",
          tip: "'I have A cold' ✓ Always use 'a'! 'I have the cold' ❌ is wrong!"
        },
        {
          type: "input",
          q: "Complete: 'She has ___ flu.' (article)",
          a: "the"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo gripe'",
          words: ["I", "have", "the", "flu."],
          a: "I have the flu.",
          successtip: "✓ THE FLU! Always 'THE'! 🦠 'I have THE flu' ✓. But 'I have A cold' ✓. Different articles!"
        },
        {
          type: "mc",
          q: "'I have ___ fever.' (tengo fiebre)",
          options: ["a", "the", "no article"],
          a: "a",
          tip: "'I have A fever' = tengo fiebre. Can also say 'I have A temperature'!"
        },
        {
          type: "input",
          q: "Complete: 'He has a high ___.' (fiebre)",
          a: "fever / temperature"
        },
        {
          type: "mc",
          q: "More serious: ___ or ___?",
          options: ["Flu is worse than cold", "Cold is worse than flu", "Same"],
          a: "Flu is worse than cold",
          successtip: "✓ FLU = WORSE! Important! 💉 Cold = stuffy nose, mild. Flu = high fever, body aches, very sick! Flu can be dangerous!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella tiene resfriado'",
          words: ["She", "has", "a", "cold."],
          a: "She has a cold."
        },
        {
          type: "input",
          q: "Complete: 'They ___ the flu.' (have/has - plural)",
          a: "have"
        },
        {
          type: "mc",
          q: "Winter in Canada: 'It's flu season. Many people ___ getting sick.'",
          options: ["are (continuous - happening now)", "is", "have"],
          a: "are (continuous - happening now)",
          tip: "Flu season = temporada de gripe (Oct-Mar in Canada). 'Are getting sick' = están enfermándose (continuous)!"
        }
      ]
    },

    // SET 5: Symptoms Vocabulary
    {
      id: "m4-u11-set5",
      title: "Set 5: Describing Symptoms",
      questions: [
        {
          type: "match",
          q: "Match symptom to Spanish:",
          pairs: [
            { left: "cough", right: "tos" },
            { left: "runny nose", right: "nariz que moquea" },
            { left: "sore throat", right: "dolor de garganta" }
          ],
          a: "completed",
          tip: "These are the classic cold/flu symptoms! Essential for pharmacy and doctor!"
        },
        {
          type: "mc",
          q: "'I have a ___.' (tos)",
          options: ["cough", "caugh", "couf"],
          a: "cough",
          tip: "Cough = tos (noun). To cough = toser (verb). Pronunciation = 'koff' (like 'off')!"
        },
        {
          type: "input",
          q: "Complete: 'She has a ___ nose.' (runny = que moquea)",
          a: "runny"
        },
        {
          type: "order",
          q: "Ordena: 'Tengo tos y dolor de garganta'",
          words: ["I", "have", "a", "cough", "and", "a", "sore", "throat."],
          a: "I have a cough and a sore throat.",
          successtip: "✓ COMBO SYMPTOMS! Natural! 🤧 List multiple symptoms with 'and'. This is how you describe at doctor/pharmacy!"
        },
        {
          type: "mc",
          q: "'My nose is running' means:",
          options: ["I have a runny nose (nariz moquea)", "My nose is exercising", "It's correct grammar"],
          a: "I have a runny nose (nariz moquea)",
          tip: "'My nose is running' = está mocosa. Can also say 'I have a runny nose'. Same meaning!"
        },
        {
          type: "input",
          q: "Complete: 'He ___ sneezing a lot.' (is/are - continuous)",
          a: "is"
        },
        {
          type: "mc",
          q: "'I'm coughing' uses continuous because:",
          options: [
            "It's happening right now (estoy tosiendo)",
            "It's a routine",
            "It's wrong"
          ],
          a: "It's happening right now (estoy tosiendo)",
          tip: "Symptoms happening NOW = continuous! 'I'm coughing' = estoy tosiendo (en este momento)!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está estornudando mucho'",
          words: ["She's", "sneezing", "a", "lot."],
          a: "She's sneezing a lot.",
          successtip: "✓ SNEEZING! Allergies too! 🤧 Sneeze = estornudar. Common with cold/allergies. 'Bless you!' when someone sneezes!"
        },
        {
          type: "input",
          q: "Complete: 'They have ___ throats.' (sore = adolorida - plural)",
          a: "sore"
        },
        {
          type: "mc",
          q: "Doctor asks: 'What symptoms do you have?' You say:",
          options: [
            "'I have a fever, cough, and sore throat'",
            "'I'm have symptoms'",
            "'Yes'"
          ],
          a: "'I have a fever, cough, and sore throat'",
          tip: "List your symptoms clearly! Doctor needs to know ALL symptoms. Be specific!"
        }
      ]
    },

    // SET 6: At the Doctor's Office
    {
      id: "m4-u11-set6",
      title: "Set 6: Doctor Appointments",
      questions: [
        {
          type: "match",
          q: "Match question to context:",
          pairs: [
            { left: "What's wrong?", right: "¿Qué te pasa? (general)" },
            { left: "What's the matter?", right: "¿Qué tienes? (same as wrong)" },
            { left: "How long have you felt this way?", right: "¿Cuánto tiempo? (duration)" }
          ],
          a: "completed",
          tip: "'What's wrong?' and 'What's the matter?' = SAME meaning! Both ask '¿qué tienes?'"
        },
        {
          type: "mc",
          q: "Doctor: 'What's the matter?' You: '___'",
          options: [
            "'I have a bad headache' (describe problem)",
            "'Yes'",
            "'The matter is headache'"
          ],
          a: "'I have a bad headache' (describe problem)",
          tip: "Describe the problem directly! 'I have...' or 'I'm feeling...' Start with symptoms!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ feeling dizzy.' (am/are)",
          a: "am"
        },
        {
          type: "order",
          q: "Ordena: 'Me duele la cabeza desde ayer'",
          words: ["I've", "had", "a", "headache", "since", "yesterday."],
          a: "I've had a headache since yesterday.",
          successtip: "✓ DURATION! Time info! ⏰ 'Since yesterday' = desde ayer. Doctors ALWAYS ask 'How long?' Be ready to answer!"
        },
        {
          type: "mc",
          q: "Doctor: 'Where does it hurt?' You: '___'",
          options: [
            "'My stomach hurts' or 'Here' (point)",
            "'Yes, it hurts'",
            "'The hurt is stomach'"
          ],
          a: "'My stomach hurts' or 'Here' (point)",
          tip: "Be specific! Point and say 'Here' or name body part. Doctor needs to know location!"
        },
        {
          type: "input",
          q: "Complete: Doctor: 'Are you taking any medication?' You: 'Yes, I ___ taking Tylenol.' (am/is)",
          a: "am"
        },
        {
          type: "mc",
          q: "'I ___ a prescription for antibiotics.' (need)",
          options: ["need", "am needing", "needs"],
          a: "need",
          successtip: "✓ PRESCRIPTION! Important word! 💊 Prescription = receta médica. In Canada, you NEED prescription for most medicine!"
        },
        {
          type: "order",
          q: "Ordena: Doctor: '¿Tienes alergias?'",
          words: ["Do", "you", "have", "any", "allergies?"],
          a: "Do you have any allergies?"
        },
        {
          type: "input",
          q: "Complete: 'I ___ allergic to penicillin.' (am/have)",
          a: "am"
        },
        {
          type: "mc",
          q: "Canadian system: 'Do you have a family doctor?' means:",
          options: [
            "Do you have a regular doctor? (¿Tienes médico de cabecera?)",
            "Is your family a doctor?",
            "Do you have medical family?"
          ],
          a: "Do you have a regular doctor? (¿Tienes médico de cabecera?)",
          tip: "Family doctor = médico de cabecera/familia. In Canada, you should register with one! Walk-in clinics for emergencies!"
        }
      ]
    },

    // SET 7: At the Pharmacy
    {
      id: "m4-u11-set7",
      title: "Set 7: Getting Medicine",
      questions: [
        {
          type: "match",
          q: "Match medicine type to Spanish:",
          pairs: [
            { left: "pain reliever", right: "analgésico" },
            { left: "cough syrup", right: "jarabe para la tos" },
            { left: "antibiotics", right: "antibióticos" }
          ],
          a: "completed",
          tip: "Over-the-counter (sin receta) vs prescription (con receta). Know the difference!"
        },
        {
          type: "mc",
          q: "Pharmacy: 'I need something for ___.' (dolor de cabeza)",
          options: ["a headache", "headache", "the headache"],
          a: "a headache",
          tip: "'Something for A headache' = algo para el dolor de cabeza. Common pharmacy phrase!"
        },
        {
          type: "input",
          q: "Complete: 'Do you have anything for ___ cough?' (a/the)",
          a: "a"
        },
        {
          type: "order",
          q: "Ordena: 'Necesito medicina para la tos'",
          words: ["I", "need", "cough", "medicine."],
          a: "I need cough medicine.",
          successtip: "✓ COUGH MEDICINE! Generic term! 💊 Cough medicine = medicina para la tos (can be syrup, pills, etc). Ask for this at pharmacy!"
        },
        {
          type: "mc",
          q: "'I have a prescription to ___.' (surtir)",
          options: ["fill", "feel", "full"],
          a: "fill",
          tip: "'Fill a prescription' = surtir una receta. Key pharmacy phrase! 'I need to fill this' ✓"
        },
        {
          type: "input",
          q: "Complete: 'How ___ do I take this?' (often = con qué frecuencia)",
          a: "often"
        },
        {
          type: "mc",
          q: "Pharmacist: 'Take ___ pills ___ a day.' (dosage)",
          options: ["two, three times", "two pills, three day", "twice, three"],
          a: "two, three times",
          successtip: "✓ DOSAGE! Critical! ⚕️ 'Two pills, three times a day' = 2 pastillas, 3 veces al día. With food? Before bed? LISTEN carefully!"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuánto cuesta esto?'",
          words: ["How", "much", "does", "this", "cost?"],
          a: "How much does this cost?"
        },
        {
          type: "input",
          q: "Complete: 'Does my insurance ___ this?' (cover = cubrir)",
          a: "cover"
        },
        {
          type: "mc",
          q: "OTC vs Prescription: Which needs doctor?",
          options: [
            "Prescription only (with receta)",
            "OTC only (sin receta)",
            "Both need doctor"
          ],
          a: "Prescription only (with receta)",
          tip: "OTC = Over-The-Counter = sin receta (Tylenol, Advil). Prescription = con receta (antibiotics). Know which you need!"
        }
      ]
    },

    // SET 8: Calling In Sick to Work
    {
      id: "m4-u11-set8",
      title: "Set 8: Professional Sick Calls",
      questions: [
        {
          type: "mc",
          q: "Calling boss: 'Hi, this is Maria. I'm calling because ___.'",
          options: [
            "I'm not feeling well today",
            "I'm dying",
            "I'm super sick"
          ],
          a: "I'm not feeling well today",
          tip: "Professional and polite! 'Not feeling well' = Canadian standard phrase for calling in sick!"
        },
        {
          type: "input",
          q: "Complete: 'I won't be able to ___ in today.' (come/coming)",
          a: "come"
        },
        {
          type: "order",
          q: "Ordena: 'No podré ir al trabajo hoy'",
          words: ["I", "won't", "be", "able", "to", "come", "to", "work", "today."],
          a: "I won't be able to come to work today.",
          successtip: "✓ PROFESSIONAL PHRASE! Perfect! 💼 'Won't be able to come' = more professional than 'can't come'. Use this phrase!"
        },
        {
          type: "mc",
          q: "Boss: 'When do you think you'll be back?' You: '___'",
          options: [
            "'I hope to be back tomorrow'",
            "'I don't know'",
            "'Never'"
          ],
          a: "'I hope to be back tomorrow'",
          tip: "Give estimate! 'I hope to be back tomorrow/Monday' = show you WANT to return. Professional!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ a doctor's appointment this afternoon.' (have/has)",
          a: "have"
        },
        {
          type: "mc",
          q: "'I have ___ flu and I don't want to get others sick.'",
          options: ["the", "a", "no article"],
          a: "the",
          successtip: "✓ CONSIDERATE! Canadian workplace culture! 🇨🇦 Mention not wanting to spread germs = shows responsibility. Bosses appreciate this!"
        },
        {
          type: "order",
          q: "Ordena: 'Te mantendré informado'",
          words: ["I'll", "keep", "you", "updated."],
          a: "I'll keep you updated.",
          successtip: "✓ KEEP UPDATED! Professional! 📱 This phrase = te mantendré informado. Perfect ending for sick call. Shows responsibility!"
        },
        {
          type: "input",
          q: "Complete: 'Is there anything urgent I ___ to know about?' (need/needs)",
          a: "need"
        },
        {
          type: "mc",
          q: "In Canada, you ___ usually need doctor's note for 1-2 sick days.",
          options: ["don't (most companies)", "do (always)", "sometimes"],
          a: "don't (most companies)",
          tip: "Usually NO doctor's note for 1-2 days! But 3+ days might need one. Check your company policy!"
        },
        {
          type: "mc",
          q: "Boss says 'Feel better!' You respond:",
          options: [
            "'Thank you, I appreciate it'",
            "'Okay'",
            "'You too'"
          ],
          a: "'Thank you, I appreciate it'",
          tip: "'Feel better!' = que te mejores. Respond with thanks! 'Thank you' or 'Thanks, I appreciate it' ✓"
        }
      ]
    },

    // SET 9: Emergency & Serious Situations
    {
      id: "m4-u11-set9",
      title: "Set 9: Emergencies & 911",
      questions: [
        {
          type: "match",
          q: "Match emergency to action:",
          pairs: [
            { left: "Chest pain", right: "Call 911 immediately!" },
            { left: "Severe bleeding", right: "Call 911 immediately!" },
            { left: "Difficulty breathing", right: "Call 911 immediately!" }
          ],
          a: "completed",
          tip: "These are EMERGENCIES! Don't wait! Call 911 (Canada) or go to ER (Emergency Room)!"
        },
        {
          type: "mc",
          q: "'I'm having chest ___.' (dolor de pecho - emergency!)",
          options: ["pain", "ache", "hurt"],
          a: "pain",
          successtip: "✓ CHEST PAIN! EMERGENCY! 🚨 Chest pain = possible heart attack! Call 911 immediately! Never wait with chest pain!"
        },
        {
          type: "input",
          q: "Complete: 'I can't ___.' (breathe = respirar - emergency!)",
          a: "breathe"
        },
        {
          type: "order",
          q: "Ordena: 911: '¿Cuál es tu emergencia?'",
          words: ["What's", "your", "emergency?"],
          a: "What's your emergency?"
        },
        {
          type: "mc",
          q: "911: 'Where are you located?' You say:",
          options: [
            "Your exact address + building/apt number",
            "'At home'",
            "'In Toronto'"
          ],
          a: "Your exact address + building/apt number",
          tip: "EXACT ADDRESS! 911 needs exact location! Include apartment/unit number, cross streets if possible!"
        },
        {
          type: "input",
          q: "Complete: '911, what's your ___?' (emergency = emergencia)",
          a: "emergency"
        },
        {
          type: "mc",
          q: "'Someone is unconscious' means:",
          options: [
            "No está consciente (not awake/responsive)",
            "They're sleeping",
            "They're tired"
          ],
          a: "No está consciente (not awake/responsive)",
          successtip: "✓ UNCONSCIOUS! Critical word! 💀 Unconscious = inconsciente/no responde. Medical emergency! Call 911!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella no está respirando'",
          words: ["She's", "not", "breathing."],
          a: "She's not breathing."
        },
        {
          type: "input",
          q: "Complete: 'He's ___ a lot.' (bleeding = sangrando - emergency)",
          a: "bleeding"
        },
        {
          type: "mc",
          q: "ER vs Walk-in clinic: Go to ER if:",
          options: [
            "Life-threatening (chest pain, bleeding, can't breathe)",
            "You have a cold",
            "You have a headache"
          ],
          a: "Life-threatening (chest pain, bleeding, can't breathe)",
          tip: "ER = Emergency Room = for EMERGENCIES only! Walk-in = for regular sick. Don't go ER for cold! It's expensive + long wait!"
        }
      ]
    },

    // SET 10: Health Insurance & System
    {
      id: "m4-u11-set10",
      title: "Set 10: Canadian Healthcare System",
      questions: [
        {
          type: "match",
          q: "Match term to meaning:",
          pairs: [
            { left: "Health card", right: "Tarjeta de salud provincial" },
            { left: "OHIP (Ontario)", right: "Ontario Health Insurance" },
            { left: "Walk-in clinic", right: "Clínica sin cita" }
          ],
          a: "completed",
          tip: "Every province has different name! OHIP (ON), MSP (BC), Alberta Health, etc. Get yours ASAP!"
        },
        {
          type: "mc",
          q: "'Do you have your health ___?' (at doctor's office)",
          options: ["card", "insurance", "number"],
          a: "card",
          tip: "Health card = tarjeta de salud. ALWAYS bring it to doctor/hospital! They scan it every visit!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ to see a doctor.' (need/needs)",
          a: "need"
        },
        {
          type: "order",
          q: "Ordena: '¿Aceptan walk-ins o necesito cita?'",
          words: ["Do", "you", "take", "walk-ins", "or", "do", "I", "need", "an", "appointment?"],
          a: "Do you take walk-ins or do I need an appointment?",
          successtip: "✓ WALK-IN! Important concept! 🚶 Walk-in = sin cita (first-come, first-served). Family doctor = need appointment usually!"
        },
        {
          type: "mc",
          q: "'I'd like to make an ___.' (cita médica)",
          options: ["appointment", "appointmen", "cita"],
          a: "appointment",
          tip: "'Make an appointment' = hacer una cita. Key phrase! 'Book an appointment' = same thing!"
        },
        {
          type: "input",
          q: "Complete: 'What's the earliest ___ available?' (appointment)",
          a: "appointment"
        },
        {
          type: "mc",
          q: "In Canada, prescription medicine costs money UNLESS you have:",
          options: [
            "Private insurance from work",
            "Only health card",
            "Nothing - it's all free"
          ],
          a: "Private insurance from work",
          successtip: "✓ KEY INFO! Health system! 💰 Doctor visits = FREE with health card. BUT prescriptions = you pay! Unless private insurance covers it!"
        },
        {
          type: "order",
          q: "Ordena: '¿Mi seguro cubre esto?'",
          words: ["Does", "my", "insurance", "cover", "this?"],
          a: "Does my insurance cover this?"
        },
        {
          type: "input",
          q: "Complete: 'I don't have ___.' (insurance = seguro médico)",
          a: "insurance"
        },
        {
          type: "mc",
          q: "Waiting for health card (3 months): Where can you go?",
          options: [
            "Walk-in clinics (pay out of pocket) or ER (free emergency)",
            "Only ER",
            "Nowhere"
          ],
          a: "Walk-in clinics (pay out of pocket) or ER (free emergency)",
          tip: "New immigrants wait 3 months for health card! Walk-in clinics charge $70-150 without card. ER = free emergency only!"
        }
      ]
    }
  ],
// ===================================================================
// READING SET: Ricardo's Sick Week (Health & Pharmacy)
// ===================================================================
readingSet: {
  id: "m4-u11-reading",
  title: "Reading: Dealing with the Flu",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The First Symptoms (preguntas 1, 2, 3, 4, 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Bad Start**

Hi, my name is Ricardo. I want to tell you about a difficult week I had last month when I got really sick. It started on Monday morning. I woke up and I wasn't feeling well. My throat was sore and I had a terrible headache. I thought maybe it was just a cold, so I went to work anyway. Bad decision!

By Tuesday, I was feeling much worse. My whole body hurt. I had a high fever—39 degrees Celsius! I was coughing a lot and my nose was running like crazy. My coworker said, "Ricardo, you look terrible. Go home!" So I called my boss and said, "I'm sorry, but I'm not feeling well. I need to go home." She said, "No problem, feel better!"`
    },
    {
      type: "mc",
      q: "¿Cuántas frases relacionadas con la salud utiliza Ricardo en estos párrafos?",
      options: [
        "Muchas (I'm not feeling well, fever, sore throat, etc.)",
        "Solo 1 o 2",
        "Ninguna"
      ],
      a: "Muchas (I'm not feeling well, fever, sore throat, etc.)",
      successtip: "✓ VOCABULARY!\nRicardo usa muchas expresiones útiles para describir una enfermedad. 🤒"
    },
    {
      type: "mc",
      q: "En la frase 'I wasn't feeling well', ¿por qué usa el Pasado Continuo?",
      options: [
        "Describe cómo se sentía en ese momento del pasado (estado en progreso)",
        "Es un error gramatical",
        "Debería ser 'I didn't feel'"
      ],
      a: "Describe cómo se sentía en ese momento del pasado (estado en progreso)",
      successtip: "✓ PAST CONTINUOUS!\nDescribe un estado que duraba en ese momento. ⏳"
    },
    {
      type: "mc",
      q: "¿Por qué dice 'I have a high fever' y no 'has'?",
      options: [
        "Con 'I' siempre se usa HAVE",
        "Debería ser 'has'",
        "Es un error del texto"
      ],
      a: "Con 'I' siempre se usa HAVE",
      successtip: "✓ SUBJECT-VERB AGREEMENT!\nI/You/We/They = Have. He/She/It = Has. 🎯"
    },
    {
      type: "mc",
      q: "¿Qué significa la expresión 'My nose was running'?",
      options: [
        "Le goteaba la nariz (secreción nasal)",
        "Su nariz estaba haciendo ejercicio",
        "Es una expresión incorrecta"
      ],
      a: "Le goteaba la nariz (secreción nasal)",
      successtip: "✓ RUNNING NOSE!\n'A runny nose' es como decimos 'moco' o secreción nasal en inglés. 🤧"
    },
    {
      type: "mc",
      q: "¿Por qué es importante usar la frase 'I'm not feeling well' en Canadá?",
      options: [
        "Es la forma profesional y educada de decir que estás enfermo",
        "Porque suena más dramático",
        "No es importante, puedes decir cualquier cosa"
      ],
      a: "Es la forma profesional y educada de decir que estás enfermo",
      successtip: "✓ CULTURAL TIP!\nEs la frase estándar para avisar a tu jefe o amigos que no te sientes bien. 💼"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: At the Clinic (preguntas 7 y 9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Diagnosis**

I stayed home on Wednesday and Thursday. I was sleeping most of the time. I wasn't eating much because I didn't have any appetite. My wife took me to a walk-in clinic on Wednesday afternoon. 

The doctor asked, "What's the matter?" She checked my temperature and looked at my throat. She said, "You have the flu. It's going around right now. You need to rest and drink lots of fluids."`
    },
    {
      type: "mc",
      q: "¿Qué significa la expresión 'It's going around'?",
      options: [
        "Mucha gente se está contagiando (está circulando)",
        "El virus camina en círculos",
        "Significa que el virus ya se fue"
      ],
      a: "Mucha gente se está contagiando (está circulando)",
      successtip: "✓ GOING AROUND!\nSe usa cuando hay un brote de gripe en la comunidad. 🦠"
    },
    {
      type: "mc",
      q: "¿Por qué dice 'I was sleeping most of the time' en lugar de 'I slept'?",
      options: [
        "Para enfatizar que la acción fue continua durante esos días",
        "Es un error del texto",
        "Porque 'slept' no existe"
      ],
      a: "Para enfatizar que la acción fue continua durante esos días",
      successtip: "✓ EMPHASIS!\nEl continuo ayuda a dar la idea de que fue algo constante durante su recuperación. 😴"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: At the Pharmacy (preguntas 6 y 8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Prescription**

I went to the pharmacy to fill my prescription. The pharmacist explained how to take the pills and the cough syrup. I asked, "How much does this cost?" She said, "Your insurance covers most of it. You pay $15." 

On Friday, I was feeling a little better. My fever was gone but I still had a cough. I called my boss to update her.`
    },
    {
      type: "mc",
      q: "¿Qué significa 'Fill a prescription'?",
      options: [
        "Surtir una receta médica en la farmacia",
        "Escribir una receta",
        "Pagar por una consulta médica"
      ],
      a: "Surtir una receta médica en la farmacia",
      successtip: "✓ PHARMACY PHRASE!\n'To fill a prescription' es el término técnico para obtener tus medicinas. 💊"
    },
    {
      type: "mc",
      q: "En partes del texto, Ricardo usa el Presente Simple ('I wake up, I go') para contar la historia. ¿Por qué?",
      options: [
        "Para dar un efecto dramático o narrativo (presente narrativo)",
        "Porque no sabe usar el pasado",
        "Porque las acciones ocurren en el futuro"
      ],
      a: "Para dar un efecto dramático o narrativo (presente narrativo)",
      successtip: "✓ NARRATIVE PRESENT!\nEs una técnica común para hacer que una historia se sienta más viva. 📖"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Conclusion & Advice (pregunta 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Lessons Learned**

What did I learn? First, when you're sick, STAY HOME! Don't get others sick. Second, in Canada, you can usually take sick days. Third, get flu shots! The pharmacy offers them for free with health insurance.

If you're new to Canada, learn these phrases: "I'm not feeling well," "What's the matter?", "I need to fill a prescription." You'll need them!`
    },
    {
      type: "mc",
      q: "¿Qué consejos prácticos da Ricardo al final de su historia?",
      options: [
        "Aprender frases clave, quedarse en casa si estás enfermo y vacunarse",
        "No ir al médico nunca",
        "Trabajar aunque estés enfermo"
      ],
      a: "Aprender frases clave, quedarse en casa si estás enfermo y vacunarse",
      successtip: "✓ IMMIGRANT ADVICE!\nExcelentes consejos para cualquier recién llegado a Canadá. 🏆"
    }
  ]
},

  // SET 12: Unit Review
  reviewSet: {
    id: "m4-u11-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match structure to use:",
        pairs: [
          { left: "I HAVE + [ache/illness]", right: "I have a headache / I have the flu" },
          { left: "My + body part + HURTS", right: "My head hurts / My legs hurt" },
          { left: "I AM/FEEL + adjective", right: "I'm sick / I feel terrible" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Article rule: 'I have ___ cold' vs 'I have ___ flu'",
        options: [
          "a cold, the flu (different articles!)",
          "the cold, a flu",
          "cold, flu (no articles)"
        ],
        a: "a cold, the flu (different articles!)",
        tip: "Memorize! 'A cold' but 'THE flu' ✓ Different articles for different illnesses!"
      },
      {
        type: "mc",
        q: "Singular vs Plural: 'My head ___' vs 'My legs ___'",
        options: [
          "hurts, hurt (singular = +s, plural = no s)",
          "hurt, hurts",
          "hurts, hurts"
        ],
        a: "hurts, hurt (singular = +s, plural = no s)"
      },
      {
        type: "input",
        q: "Complete: 'I'm not ___ well today.' (feeling)",
        a: "feeling"
      },
      {
        type: "input",
        q: "Complete professional sick call: 'I ___ be able to come to work today.' (won't)",
        a: "won't"
      },
      {
        type: "order",
        q: "Ordena: 'Necesito algo para el dolor de cabeza'",
        words: ["I", "need", "something", "for", "a", "headache."],
        a: "I need something for a headache."
      },
      {
        type: "mc",
        q: "Emergency symptoms that need 911:",
        options: [
          "Chest pain, can't breathe, severe bleeding, unconscious",
          "Headache, cold, cough",
          "Stomachache"
        ],
        a: "Chest pain, can't breathe, severe bleeding, unconscious",
        tip: "Know emergencies! Call 911 for life-threatening! Don't wait with chest pain or breathing problems!"
      },
      {
        type: "mc",
        q: "'Fill a prescription' means:",
        options: [
          "Get medicine at pharmacy (surtir receta)",
          "Write prescription",
          "Pay doctor"
        ],
        a: "Get medicine at pharmacy (surtir receta)"
      },
      {
        type: "mc",
        q: "Canadian health system: Doctor visit with health card =",
        options: [
          "FREE (covered by provincial health)",
          "Always costs money",
          "$100"
        ],
        a: "FREE (covered by provincial health)",
        tip: "Doctor visits = FREE with health card! But prescriptions = you pay (unless private insurance)!"
      },
      {
        type: "mc",
        q: "Final mastery scenario: 'You wake up with 39° fever, terrible cough, sore throat, body aches. You need to: 1) Call boss, 2) Go to clinic, 3) Get medicine. What do you say?'\n\nComplete conversation:",
        options: [
          "Boss: 'Hi, I'm not feeling well. I have a high fever and cough. I won't be able to come in today.' | Doctor: 'I have a fever, cough, and sore throat. My whole body hurts.' | Pharmacy: 'I need to fill this prescription. How often do I take it?'",
          "Boss: 'I'm dying!' | Doctor: 'I'm sick.' | Pharmacy: 'Give me medicine.'",
          "Say nothing"
        ],
        a: "Boss: 'Hi, I'm not feeling well. I have a high fever and cough. I won't be able to come in today.' | Doctor: 'I have a fever, cough, and sore throat. My whole body hurts.' | Pharmacy: 'I need to fill this prescription. How often do I take it?'",
        successtip: "✓ UNIT 11 MASTERED! Health communication complete! 🎉 From basic 'I have a headache' to full medical conversations! You can now: describe symptoms, call in sick professionally, talk to doctors, get prescriptions, handle emergencies, navigate Canadian healthcare! This is SURVIVAL English! You went from A2 to solid B1 in health situations! 💪🏥🇨🇦 These phrases can literally save your life!"
      }
    ]
  }
};
